import path from "path";
import { config as loadEnv } from "dotenv";
import {
  DEVICE_SIZES,
  IMAGE_SIZES,
  getAllowedImageHosts,
} from "./src/utils/imageHosts.js";

const rootDir = process.cwd();
// console.log("Root directory:", rootDir);

// Load environment variables from a specific .env file
loadEnv({ path: path.resolve(rootDir, "../../.env") });
// console.log(process.env.WP_API_URL)

// Only wire Thumbor up when it is actually configured — otherwise the custom
// loader points every image at a route that can't sign, turning a missing env
// var into a broken page instead of an unoptimized one.
const useThumbor =
  process.env.DISABLE_THUMBOR !== "true" &&
  !!process.env.THUMBOR_HOST?.trim() &&
  !!process.env.THUMBOR_SECURITY_KEY?.trim();

/** @type {import('next').NextConfig} */
const nextConfig = {
  env: {
    WP_API_URL: process.env.WP_API_URL,
    BUILD_ALL_POST: process.env.BUILD_ALL_POST,
    REVALIDATE_AFTER: process.env.REVALIDATE_AFTER || 120,
    NEXT_PUBLIC_LOCALES: process.env.NEXT_PUBLIC_LOCALES || "de",
    NEXT_PUBLIC_DEFAULT_LOCALE: process.env.NEXT_PUBLIC_DEFAULT_LOCALE || "de",
    BASIC_AUTH_USERNAME: process.env.BASIC_AUTH_USERNAME || "",
    BASIC_AUTH_PASSWORD: process.env.BASIC_AUTH_PASSWORD || "",
    PRODUCTION_BROWSER_SOURCE_MAPS:
      process.env.PRODUCTION_BROWSER_SOURCE_MAPS || "false",
    NEXT_PUBLIC_INTERNAL_ORIGINS:
      process.env.NEXT_PUBLIC_INTERNAL_ORIGINS || "",
    DEFAULT_LOCALE: process.env.NEXT_PUBLIC_DEFAULT_LOCALE || "de", // needed for middleware
    CHECK_ACCESSIBILITY: process.env.CHECK_ACCESSIBILITY || "false",
    PRIVATE_PAGE_PREVIEW_SECRET: process.env.PRIVATE_PAGE_PREVIEW_SECRET || "",
    NEXT_PUBLIC_GOOGLE_MAPS_API_KEY:
      process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || "",
    NEXT_PUBLIC_STARTPAGE_SLUG_ELEMENTS:
      process.env.NEXT_PUBLIC_STARTPAGE_SLUG_ELEMENTS || "",
    NEXT_PUBLIC_PUBLIC_URL_ELEMENTS:
      process.env.NEXT_PUBLIC_PUBLIC_URL_ELEMENTS || "",
    NEXT_PUBLIC_INTERNAL_ORIGINS_ELEMENTS:
      process.env.NEXT_PUBLIC_INTERNAL_ORIGINS_ELEMENTS || "",
  },

  transpilePackages: [
    "@aleksundshantu/aunds-utils-react",
    "@aleksundshantu/aunds-cookie-plugin-react",
  ],

  
  images: {
    // sharp cannot run on the FreeBSD production hosts — confirmed both
    // native (no such binary exists) and the @img/sharp-wasm32 fallback
    // (loads, but crashes on its first real call: TypeError inside the
    // WASM/emnapi glue on libvipsVersion()). Optimizing via Thumbor instead
    // of in-process sharp avoids that entirely — Next.js never touches sharp.
    //
    // DISABLE_THUMBOR=true switches back to Next's built-in (sharp-based)
    // loader. The host/key check does the same when Thumbor is not configured
    // at all — an unoptimized page beats a page of broken images.
    ...(useThumbor
      ? {
          loader: "custom",
          loaderFile: "./src/utils/thumborLoader.ts",
        }
      : {}),

    // Kept even though a custom loader bypasses remotePatterns: this is what
    // the DISABLE_THUMBOR fallback needs. Same source of truth as the signing
    // route's allowlist — see src/utils/imageHosts.js.
    remotePatterns: getAllowedImageHosts().map((hostname) => ({
      protocol: "https",
      hostname,
      pathname: "/**",
    })),
    deviceSizes: DEVICE_SIZES,
    imageSizes: IMAGE_SIZES,
    minimumCacheTTL: 604800,
  },

  i18n: {
    locales: (process.env.NEXT_PUBLIC_LOCALES || "de").split(","),
    defaultLocale: process.env.NEXT_PUBLIC_DEFAULT_LOCALE || "de",
  },

  compress: true,

  experimental: {
    scrollRestoration: true,
    esmExternals: false, // required for many monorepo setups
    // NEXT_BUILD_WORKERS overrides Next's default worker-process count for
    // static generation (os.cpus().length - 1). Leave unset to keep that
    // default; tune via CI/CD variable if the build needs fewer/more workers.
    ...(process.env.NEXT_BUILD_WORKERS
      ? { cpus: Number(process.env.NEXT_BUILD_WORKERS) }
      : {}),
  },

  sassOptions: {
    includePaths: [
      path.join(rootDir, "src", "styles"), // import stylesheets from /src/styles
      path.join(rootDir, "..", "..", "node_modules"), // import stylesheets from root node_modules
    ],
    additionalData: `@use "global-entrypoint" as *;`,
  },

  // Applies to both "Collecting page data" (getStaticPaths) and each individual
  // page render. The WP backend answers in seconds, not milliseconds, so a
  // 120s ceiling aborted the whole build on slow days — two strikes and Next
  // fails hard. Tunable via CI/CD variable.
  staticPageGenerationTimeout: Number(
    process.env.STATIC_PAGE_GENERATION_TIMEOUT || 300,
  ),

  webpack(config) {
    config.module.rules.push(
      {
        test: /\.svg$/,
        issuer: { and: [/\.(js|ts)x?$/] },
        use: ["@svgr/webpack"],
      },
      {
        test: /\.module\.scss$/,
        include: [path.resolve(rootDir, "node_modules/@aleksundshantu")],
        use: [
          "style-loader",
          {
            loader: "css-loader",
            options: {
              modules: true,
            },
          },
          "sass-loader",
        ],
      },
    );
    return config;
  },

  trailingSlash: true,

  productionBrowserSourceMaps:
    process.env.PRODUCTION_BROWSER_SOURCE_MAPS === "true",
  eslint: {
    ignoreDuringBuilds: true,
  },

  poweredByHeader: false,
};

export default nextConfig;
